import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Portal-free modal / drawer — Field Notes rebuild (epic #950 PR-8,
 * formerly the console-shared `createModal` factory instantiation).
 * Keeps the factory's focus-trap and escape/backdrop-close contract:
 * Escape closes, Tab cycles within the panel, first focusable element
 * receives focus on open.
 */
interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly titleId: string;
  readonly children: ReactNode;
  readonly closeOnBackdrop?: boolean;
  readonly testId?: string;
  readonly variant?: 'center' | 'drawer';
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  titleId,
  children,
  closeOnBackdrop = true,
  testId,
  variant = 'center',
}: Props): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const panel = panelRef.current;
      if (panel === null) {
        return;
      }
      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return;
    }
    const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (first !== null) {
      first.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="presentation"
      data-testid={testId}
      className={
        variant === 'drawer'
          ? 'fixed inset-0 z-50 flex items-stretch justify-end bg-ink/25'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4'
      }
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={
          variant === 'drawer'
            ? 'h-full w-full max-w-[920px] overflow-hidden border-l border-line bg-card text-ink'
            : 'w-full max-w-lg rounded-xl border border-line bg-card text-ink'
        }
      >
        {children}
      </div>
    </div>
  );
}
